<template>
  <div class="fixed inset-0 flex justify-center items-center z-50">

    <div class="flex items-center justify-center min-h-screen p-4">
      <div class="fixed inset-0 bg-black/50" @click="$emit('close')"></div>

      <div class="bg-white rounded-lg max-w-6xl w-full relative z-10 max-h-[90vh] overflow-hidden flex flex-col">
        <!-- Header -->
        <div class="px-6 py-4 border-b border-gray-200">
          <h3 class="text-lg font-semibold text-gray-900">
            {{ isNew ? 'Create' : 'Edit' }} Clinical Summary Template
          </h3>
          <p class="text-sm text-gray-500 mt-1">
            Configure the AI prompt, output schema, and data retrieval settings
          </p>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto px-6 py-4">
          <div class="space-y-6">
            <!-- Basic Information -->
            <div class="bg-gray-50 rounded-lg p-4">
              <h4 class="text-sm font-semibold text-gray-900 mb-3">Basic Information</h4>
              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Template Name <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="formData.name"
                    type="text"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent"
                    placeholder="e.g., Diagnosis Summary"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    v-model="formData.description"
                    rows="2"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent"
                    placeholder="Brief description of what this template does"
                  ></textarea>
                </div>
              </div>
            </div>

            <!-- Metadata Configuration -->
            <div class="bg-gray-50 rounded-lg p-4">
              <h4 class="text-sm font-semibold text-gray-900 mb-3">Data Configuration</h4>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Template Type <span class="text-red-500">*</span>
                  </label>
                  <select
                    v-model="formData.metadata.template_type"
                    @change="updateDataFunction"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent"
                  >
                    <option value="dx">Diagnosis (dx)</option>
                    <option value="rx">Prescription (rx)</option>
                  </select>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Data Function <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="formData.metadata.data_function"
                    type="text"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 font-mono text-xs"
                    readonly
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Date Field for Sorting <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="formData.metadata.date_field_for_sorting"
                    type="text"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent font-mono text-sm"
                    placeholder="e.g., last_billed"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Default Record Limit
                  </label>
                  <input
                    v-model.number="formData.metadata.limit_to_recent"
                    type="number"
                    min="1"
                    max="100"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent"
                    placeholder="20"
                  />
                </div>
              </div>
            </div>

            <!-- LLM Prompt -->
            <div class="bg-gray-50 rounded-lg p-4">
              <div class="flex items-center justify-between mb-3">
                <h4 class="text-sm font-semibold text-gray-900">
                  LLM Prompt <span class="text-red-500">*</span>
                </h4>
                <button
                  @click="loadDefaultPrompt"
                  class="text-xs text-primary-600 hover:text-primary-700"
                >
                  Load Default for {{ formatTemplateType(formData.metadata.template_type) }}
                </button>
              </div>
              <textarea
                v-model="formData.metadata.prompt"
                rows="10"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent font-mono text-xs"
                placeholder="Enter the prompt that will be sent to the LLM..."
              ></textarea>
              <p class="text-xs text-gray-500 mt-1">
                This prompt instructs the AI on how to analyze the patient data and generate the summary.
              </p>
            </div>

            <!-- Output Schema Editor -->
            <div class="bg-gray-50 rounded-lg p-4">
              <div class="flex items-center justify-between mb-3">
                <h4 class="text-sm font-semibold text-gray-900">
                  Output Schema (JSON) <span class="text-red-500">*</span>
                </h4>
                <div class="flex items-center space-x-2">
                  <button
                    @click="loadDefaultSchema"
                    class="text-xs text-primary-600 hover:text-primary-700"
                  >
                    Load Default for {{ formatTemplateType(formData.metadata.template_type) }}
                  </button>
                  <button
                    @click="validateSchema"
                    class="text-xs text-green-600 hover:text-green-700"
                  >
                    <FontAwesomeIcon :icon="['fas', 'check-circle']" class="mr-1" />
                    Validate
                  </button>
                </div>
              </div>
              <textarea
                v-model="schemaJson"
                rows="15"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent font-mono text-xs"
                :class="{ 'border-red-500': schemaError }"
                placeholder='{\n  "type": "object",\n  "properties": { ... }\n}'
              ></textarea>
              <p v-if="schemaError" class="text-xs text-red-600 mt-1">
                {{ schemaError }}
              </p>
              <p v-else class="text-xs text-gray-500 mt-1">
                Define the JSON schema that the LLM will use to structure its output.
              </p>
            </div>

            <!-- Additional Context (Optional) -->
            <div class="bg-gray-50 rounded-lg p-4">
              <h4 class="text-sm font-semibold text-gray-900 mb-3">
                Additional Context (Optional)
              </h4>
              <textarea
                v-model="additionalContextJson"
                rows="6"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent font-mono text-xs"
                placeholder='{\n  "context_key": "context_value"\n}'
              ></textarea>
              <p class="text-xs text-gray-500 mt-1">
                Optional additional context to include in LLM calls (e.g., reference guidelines, definitions).
              </p>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
          <button
            @click="$emit('close')"
            type="button"
            class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            @click="save"
            type="button"
            :disabled="!isValid"
            class="btn-primary"
          >
            {{ isNew ? 'Create' : 'Update' }} Template
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'ClinicalSummaryTemplate_EditorModal',

  components: {
    FontAwesomeIcon
  },

  props: {
    template: {
      type: Object,
      required: true
    },
    isNew: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      formData: JSON.parse(JSON.stringify(this.template)),
      schemaJson: '',
      additionalContextJson: '{}',
      schemaError: null
    };
  },

  computed: {
    isValid() {
      return (
        this.formData.name &&
        this.formData.metadata.template_type &&
        this.formData.metadata.data_function &&
        this.formData.metadata.date_field_for_sorting &&
        this.formData.metadata.prompt &&
        this.formData.metadata.output_schema &&
        !this.schemaError
      );
    }
  },

  mounted() {
    // Initialize JSON editors
    this.schemaJson = JSON.stringify(this.formData.metadata.output_schema || {}, null, 2);
    this.additionalContextJson = JSON.stringify(this.formData.metadata.additional_context || {}, null, 2);
  },

  methods: {
    updateDataFunction() {
      const functionMap = {
        dx: {
          function: 'return_patient_detail_dx',
          dateField: 'last_billed'
        },
        rx: {
          function: 'return_patient_detail_rx',
          dateField: 'updated_at'
        }
      };

      const config = functionMap[this.formData.metadata.template_type];
      if (config) {
        this.formData.metadata.data_function = config.function;
        this.formData.metadata.date_field_for_sorting = config.dateField;
      }
    },

    loadDefaultPrompt() {
      const prompts = {
        dx: `You are a physician reviewing a patient's diagnosis history for comprehensive care management.

Analyze the diagnosis data and provide:
1. A comprehensive summary of the patient's diagnosis history
2. List active chronic conditions with clinical significance (identify patterns, comorbidities)
3. Note recent acute diagnoses
4. Identify gaps in diagnosis documentation or coding opportunities
5. Summarize HCC-related diagnoses and their implications for risk adjustment

Focus on:
- Chronic disease management opportunities
- Risk stratification and severity
- Documentation quality and specificity
- Conditions that may need annual recapture for HCC coding

Be specific with dx_codes and HCC codes where available.`,
        rx: `You are a clinical pharmacist reviewing a patient's medication history for comprehensive medication therapy management.

Analyze the prescription data and provide:
1. A concise summary of the patient's current medication regimen
2. List all active medications with their indications and clinical context
3. Note any recently discontinued medications and apparent reasons
4. Identify potential issues:
   - Drug-drug interactions
   - Duplicate therapy
   - Adherence concerns (frequent changes, gaps in refills)
   - Safety concerns (high-risk medications in elderly, contraindications)
5. Provide actionable recommendations for medication optimization

Focus on patient safety, medication appropriateness, and adherence.
Be specific about which medications are involved in any concerns.`
      };

      this.formData.metadata.prompt = prompts[this.formData.metadata.template_type] || '';
    },

    loadDefaultSchema() {
      const schemas = {
        dx: {
          type: 'object',
          properties: {
            diagnosis_summary: {
              type: 'string',
              description: 'Overall diagnosis summary'
            },
            chronic_conditions: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  condition: { type: 'string' },
                  dx_code: { type: 'string' },
                  hcc_code: { type: ['string', 'null'] },
                  last_documented: { type: 'string' },
                  clinical_significance: { type: 'string' }
                },
                required: ['condition', 'dx_code']
              }
            },
            acute_diagnoses: {
              type: 'array',
              items: { type: 'string' }
            },
            gaps_in_documentation: {
              type: 'array',
              items: { type: 'string' }
            },
            hcc_summary: {
              type: 'string'
            }
          },
          required: ['diagnosis_summary', 'chronic_conditions'],
          additionalProperties: false
        },
        rx: {
          type: 'object',
          properties: {
            medication_summary: {
              type: 'string'
            },
            active_medications: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  medication: { type: 'string' },
                  indication: { type: 'string' },
                  status: { type: 'string' },
                  clinical_note: { type: 'string' }
                },
                required: ['medication', 'status']
              }
            },
            discontinued_medications: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  medication: { type: 'string' },
                  reason_if_apparent: { type: 'string' }
                }
              }
            },
            potential_issues: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  issue_type: {
                    type: 'string',
                    enum: ['drug_interaction', 'duplicate_therapy', 'adherence_concern', 'safety_concern', 'other']
                  },
                  description: { type: 'string' }
                }
              }
            },
            recommendations: {
              type: 'array',
              items: { type: 'string' }
            }
          },
          required: ['medication_summary', 'active_medications'],
          additionalProperties: false
        }
      };

      const schema = schemas[this.formData.metadata.template_type];
      if (schema) {
        this.schemaJson = JSON.stringify(schema, null, 2);
        this.formData.metadata.output_schema = schema;
      }
    },

    validateSchema() {
      try {
        const schema = JSON.parse(this.schemaJson);
        this.formData.metadata.output_schema = schema;
        this.schemaError = null;

        // Basic validation
        if (!schema.type || schema.type !== 'object') {
          this.schemaError = 'Schema must have type: "object" at root level';
          return;
        }

        if (!schema.properties) {
          this.schemaError = 'Schema must have a "properties" object';
          return;
        }

        alert('Schema is valid!');
      } catch (error) {
        this.schemaError = `Invalid JSON: ${error.message}`;
      }
    },

    save() {
      // Parse and validate schema
      try {
        this.formData.metadata.output_schema = JSON.parse(this.schemaJson);
      } catch (error) {
        this.schemaError = `Invalid JSON: ${error.message}`;
        return;
      }

      // Parse additional context
      try {
        const context = JSON.parse(this.additionalContextJson);
        if (Object.keys(context).length > 0) {
          this.formData.metadata.additional_context = context;
        }
      } catch (error) {
        // Ignore if empty or invalid
      }

      this.$emit('save', this.formData);
    },

    formatTemplateType(type) {
      const labels = {
        dx: 'Diagnosis',
        rx: 'Prescription',
        lab: 'Laboratory'
      };
      return labels[type] || type.toUpperCase();
    }
  }
};
</script>
